/**
 * EQuIP API Client
 *
 * HTTP client for the EQuIP (Eventus Quality Incentive Program) backend API.
 */

import type {
  GridResponse,
  SourceFreshnessResponse,
  ProviderProfile,
  MeasureConstituents,
} from '@/views/equip/admin/qa-debug/types'

import type {
  ProviderPerformanceSummary,
  ProviderPerformanceDetail,
  PatientDrilldownItem,
  UserContext,
  SnapshotStatus,
  SnapshotConfigItem,
  AwardPreview,
  QuarterTimelineConfig,
  PatientMeasureDetail,
  PIMsAuditItem,
  PIMsFacilitySummary,
  AuditListItem,
  AuditDetail,
  LeadershipAggregation,
  SeniorLeadershipAggregation,
  IncentiveProjection,
  PaginatedPIMsPatients,
  AwardHistoryItem,
  ManagerOption,
  ClinicianOption,
  EDSendoutEncounterRow,
  ProgramConfigItem,
  ProgramConfigUpdate,
  ConfigAuditResponse,
  EligibilityRow,
  EligibilityUpdate,
  EligibilityRosterResponse,
  AddMissingResponse,
  BulkMarkEligibleRequest,
  BulkMarkEligibleResponse,
  EmployeeRosterResponse,
  SupervisingPhysicianOption,
  ExecutionStatus,
  SeedPreviewResponse,
  SeedExecuteResponse,
  SeedOverride,
  SeedStatusResponse,
  EligibilityCriteriaRule,
  EligibilityCriterionUpdate,
  EligibilityCriteriaAuditResponse,
  MeasureScheduleItem,
  MeasureScheduleCreate,
  MeasureScheduleUpdate,
  ValidationStatus,
  ValidationTransitionRequest,
  ExecutionListItem,
  LeaderMeasureFacility,
  LeaderMeasurePatientsResponse,
  LeaderAcoPatientsResponse,
  LeaderAuditClinician,
  AwardUpdate,
  AwardGridResponse,
  AwardQuartersResponse,
  AwardHistoryResponse,
  PimsDistribution,
} from '@/lib/equip-types'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''
const ENDPOINT = `${API_BASE_URL}/api/equip`

/**
 * Helper for fetch with credentials
 * Handles JSON parsing and error responses consistently
 * Includes 90-second timeout for all requests
 */
const fetchWithCredentials = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  // Add 90-second timeout using AbortController
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 90000)

  try {
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw Object.assign(new Error(error.detail || `HTTP ${response.status}`), { status: response.status })
    }

    return response.json() as Promise<T>
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out after 90 seconds')
    }
    // Preserve original error (including any attached HTTP status)
    if (error instanceof Error) throw error
    throw new Error('Unknown error')
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Build query string from params object
 * Handles array parameters by appending multiple query params with same key
 */
const buildQueryString = <T extends Record<string, unknown>>(params: T): string => {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        // Handle array params: provider_types=PCP&provider_types=Psychiatrist
        value.forEach((item) => {
          if (item !== undefined && item !== null && item !== '') {
            searchParams.append(key, String(item))
          }
        })
      } else {
        searchParams.set(key, String(value))
      }
    }
  })
  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

// =============================================================================
// API Client
// =============================================================================

export const equipApi = {
  /**
   * Performance endpoints
   */
  performance: {
    /**
     * List performance for all providers (optionally filtered by provider types)
     * Leadership roles see only their accessible provider types
     */
    list: (params?: { providerTypes?: string[]; year?: number; quarter?: number }) =>
      fetchWithCredentials<ProviderPerformanceSummary[]>(
        `${ENDPOINT}/performance${buildQueryString(
          params
            ? {
                provider_types: params.providerTypes,
                year: params.year,
                quarter: params.quarter,
              }
            : {}
        )}`
      ),

    /**
     * Get my own performance (current user)
     * Award amounts hidden for equip.read role
     */
    getMyPerformance: (params?: { year?: number; quarter?: number }) =>
      fetchWithCredentials<ProviderPerformanceDetail>(
        `${ENDPOINT}/performance/me${buildQueryString(
          params
            ? {
                year: params.year,
                quarter: params.quarter,
              }
            : {}
        )}`
      ),

    /**
     * Get specific provider's performance (by email)
     * Requires leadership or admin role
     */
    getProviderPerformance: (email: string, params?: { year?: number; quarter?: number }) =>
      fetchWithCredentials<ProviderPerformanceDetail>(
        `${ENDPOINT}/performance/${encodeURIComponent(email)}${buildQueryString(
          params
            ? {
                year: params.year,
                quarter: params.quarter,
              }
            : {}
        )}`
      ),
  },

  /**
   * Patient drill-down endpoints
   */
  patients: {
    /**
     * Get patient-level drill-down for a specific measure
     */
    getDrilldown: (npi: string, measure: string, params?: { year?: number; quarter?: number }) =>
      fetchWithCredentials<PatientDrilldownItem[]>(
        `${ENDPOINT}/patients/${encodeURIComponent(npi)}/${encodeURIComponent(measure)}${buildQueryString(
          params
            ? {
                year: params.year,
                quarter: params.quarter,
              }
            : {}
        )}`
      ),
  },

  /**
   * User context endpoint
   */
  context: {
    /**
     * Get current user's EQuIP context (role, accessible types, award visibility)
     */
    getMyContext: () => fetchWithCredentials<UserContext>(`${ENDPOINT}/me/context`),
  },

  /**
   * Snapshot management endpoints
   */
  snapshots: {
    /**
     * List all snapshot configurations
     */
    list: () =>
      fetchWithCredentials<SnapshotConfigItem[]>(`${ENDPOINT}/snapshots`),

    /**
     * Get snapshot status for specific year/quarter
     */
    getStatus: (year: number, quarter: number) =>
      fetchWithCredentials<SnapshotStatus>(
        `${ENDPOINT}/snapshots/status/${year}/${quarter}`
      ),

    /**
     * Create snapshot for specific year/quarter
     * Returns 202 Accepted with status_url
     */
    create: (year: number, quarter: number) =>
      fetchWithCredentials<{ accepted: boolean; message: string; statusUrl: string; executionId: number }>(
        `${ENDPOINT}/snapshots/create`,
        { method: 'POST', body: JSON.stringify({ year, quarter }) }
      ),

    /**
     * Get execution status by ID (for polling after freeze trigger)
     */
    getExecutionStatus: (executionId: number) =>
      fetchWithCredentials<ExecutionStatus>(
        `${ENDPOINT}/snapshots/status/execution/${executionId}`
      ),

    /**
     * List all executions for a year/quarter (for expanded detail view)
     */
    listExecutions: (year: number, quarter: number) =>
      fetchWithCredentials<ExecutionListItem[]>(
        `${ENDPOINT}/snapshots/${year}/${quarter}/executions`
      ),

    /**
     * @deprecated Awards are now calculated as part of create_snapshot.
     * Retained for backward compatibility only.
     */
    calculateAwards: (year: number, quarter: number) =>
      fetchWithCredentials<{ accepted: boolean; message: string; statusUrl: string }>(
        `${ENDPOINT}/snapshots/calculate-awards`,
        { method: 'POST', body: JSON.stringify({ year, quarter }) }
      ),

    /**
     * Update cutoff date for a pending snapshot config
     */
    updateCutoffDate: (year: number, quarter: number, cutoffDate: string) =>
      fetchWithCredentials<{ year: number; quarter: number; cutoffDate: string; message: string }>(
        `${ENDPOINT}/snapshots/${year}/${quarter}/cutoff-date`,
        { method: 'PUT', body: JSON.stringify({ cutoffDate }) }
      ),

    /**
     * Create a new quarter config row
     */
    createConfig: (year: number, quarter: number) =>
      fetchWithCredentials<{ year: number; quarter: number; snapshotStatus: string; message: string }>(
        `${ENDPOINT}/snapshots/config`,
        { method: 'POST', body: JSON.stringify({ year, quarter }) }
      ),

    /**
     * Download payroll ZIP for an awarded snapshot
     */
    exportPayroll: async (year: number, quarter: number): Promise<Blob> => {
      const response = await fetch(`${ENDPOINT}/snapshots/${year}/${quarter}/export`, {
        credentials: 'include',
      })
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
        throw new Error(error.detail || `HTTP ${response.status}`)
      }
      return response.blob()
    },

    /**
     * Get pre-freeze award preview (provider counts, average awards, total payout by type)
     */
    getAwardPreview: (year: number, quarter: number) =>
      fetchWithCredentials<AwardPreview>(
        `${ENDPOINT}/snapshots/${year}/${quarter}/award-preview`
      ),
  },

  /**
   * Dashboard endpoints (Phase 6)
   */
  dashboard: {
    /**
     * Get available quarters from equip_config_snapshot for the quarter selector.
     */
    getAvailableQuarters: () =>
      fetchWithCredentials<{ year: number; quarter: number; status: string }[]>(
        `${ENDPOINT}/dashboard/quarters`
      ),

    /**
     * Get the effective current quarter (respects cutoff date overrides).
     */
    getCurrentQuarter: () =>
      fetchWithCredentials<{ year: number; quarter: number }>(
        `${ENDPOINT}/dashboard/current-quarter`
      ),

    /**
     * Get quarter timeline configuration (dates for quarter end, snapshot, notification, payout)
     */
    getTimeline: (params?: { year?: number; quarter?: number }) =>
      fetchWithCredentials<QuarterTimelineConfig>(
        `${ENDPOINT}/dashboard/timeline${buildQueryString(
          params
            ? {
                year: params.year,
                quarter: params.quarter,
              }
            : {}
        )}`
      ),

    /**
     * Get patient measure detail (all quality measures for a specific patient)
     */
    getPatientMeasures: (patientId: string, year?: number, quarter?: number) =>
      fetchWithCredentials<PatientMeasureDetail>(
        `${ENDPOINT}/dashboard/patient/${encodeURIComponent(patientId)}/measures${buildQueryString(
          year !== undefined || quarter !== undefined
            ? {
                year,
                quarter,
              }
            : {}
        )}`
      ),

    /**
     * Get patient PIMs data from cache (single record per patient/quarter)
     */
    getPatientPims: (patientId: string, year: number, quarter: number) =>
      fetchWithCredentials<PIMsAuditItem | null>(
        `${ENDPOINT}/dashboard/patient/${encodeURIComponent(patientId)}/pims${buildQueryString(
          { year, quarter }
        )}`
      ),

    /**
     * Get PIMs facility summary (aggregated scores for provider's facility)
     */
    getPimsFacilitySummary: (npi: string, year?: number, quarter?: number) =>
      fetchWithCredentials<PIMsFacilitySummary[]>(
        `${ENDPOINT}/dashboard/pims/${encodeURIComponent(npi)}${buildQueryString(
          year !== undefined || quarter !== undefined
            ? {
                year,
                quarter,
              }
            : {}
        )}`
      ),

    /**
     * Get provider's audits (coding or quality audits)
     */
    getProviderAudits: (
      email: string,
      auditType: string,
      year?: number,
      quarter?: number
    ) =>
      fetchWithCredentials<AuditListItem[]>(
        `${ENDPOINT}/dashboard/audits/${encodeURIComponent(email)}${buildQueryString({
          audit_type: auditType,
          year,
          quarter,
        })}`
      ),

    /**
     * Get audit detail (questions/responses for a specific audit)
     */
    getAuditDetail: (auditId: number, auditType: string) =>
      fetchWithCredentials<AuditDetail>(
        `${ENDPOINT}/dashboard/audits/detail/${auditId}${buildQueryString({
          audit_type: auditType,
        })}`
      ),

    /**
     * Get leadership aggregation (team summary for leadership dashboard)
     * Supports optional managerEmail for scoping to a manager's team
     */
    getLeadershipAggregation: (params?: {
      providerTypes?: string[]
      year?: number
      quarter?: number
      managerEmail?: string
    }) =>
      fetchWithCredentials<LeadershipAggregation>(
        `${ENDPOINT}/dashboard/leadership${buildQueryString(
          params
            ? {
                provider_types: params.providerTypes,
                year: params.year,
                quarter: params.quarter,
                manager_email: params.managerEmail,
              }
            : {}
        )}`
      ),

    /**
     * Get senior leadership aggregation (region-level summary for senior leadership dashboard)
     * Supports optional seniorLeadEmail for admin impersonation
     */
    getSeniorLeadershipAggregation: (params: {
      year: number
      quarter: number
      seniorLeadEmail?: string
    }) =>
      fetchWithCredentials<SeniorLeadershipAggregation>(
        `${ENDPOINT}/dashboard/senior-leadership${buildQueryString({
          year: params.year,
          quarter: params.quarter,
          senior_lead_email: params.seniorLeadEmail,
        })}`
      ),

    /**
     * Get RDCOE regional incentive projection (mirrors getLeaderIncentive
     * one level up the org hierarchy). Supports optional seniorLeadEmail
     * for admin impersonation.
     */
    getRegionalIncentive: (params: {
      year: number
      quarter: number
      seniorLeadEmail?: string
    }) =>
      fetchWithCredentials<IncentiveProjection>(
        `${ENDPOINT}/dashboard/regional-incentive${buildQueryString({
          year: params.year,
          quarter: params.quarter,
          senior_lead_email: params.seniorLeadEmail,
        })}`
      ),

    /**
     * Get paginated patient-level PIMs medication detail for a facility
     */
    getPimsFacilityPatients: (
      facilityId: string,
      params?: {
        providerNpi?: string
        year?: number
        quarter?: number
        page?: number
        pageSize?: number
      }
    ) =>
      fetchWithCredentials<PaginatedPIMsPatients>(
        `${ENDPOINT}/dashboard/pims/facility/patients${buildQueryString({
          facility_id: facilityId,
          provider_npi: params?.providerNpi,
          year: params?.year,
          quarter: params?.quarter,
          page: params?.page,
          page_size: params?.pageSize,
        })}`
      ),

    /**
     * Get award history for a provider across all quarters
     */
    getAwardHistory: (providerNpi: string) =>
      fetchWithCredentials<AwardHistoryItem[]>(
        `${ENDPOINT}/dashboard/award-history/${encodeURIComponent(providerNpi)}`
      ),

    /**
     * Get encounter-level ED send-out drilldown for a teletriage provider
     */
    getEDSendoutDrilldown: (npi: string, params?: { year?: number; quarter?: number }) =>
      fetchWithCredentials<EDSendoutEncounterRow[]>(
        `${ENDPOINT}/dashboard/ed-sendout-drilldown/${encodeURIComponent(npi)}${buildQueryString(
          params
            ? {
                year: params.year,
                quarter: params.quarter,
              }
            : {}
        )}`
      ),

    /**
     * Get aggregated performance for supervised APCs (supervising physician view).
     * Response intentionally excludes all financial/award fields.
     * Requires equip.read role with cqm_apc_physician_mapping rows.
     * Admin users may pass physicianNpi to impersonate a specific supervising physician.
     */
    getSupervisedApcs: (params: { year: number; quarter: number; physicianNpi?: number }) =>
      fetchWithCredentials<LeadershipAggregation>(
        `${ENDPOINT}/dashboard/supervised-apcs${buildQueryString({
          year: params.year,
          quarter: params.quarter,
          ...(params.physicianNpi !== undefined ? { physician_npi: params.physicianNpi } : {}),
        })}`
      ),

    /**
     * Get list of supervising physicians for admin impersonation dropdown.
     * Admin-only endpoint — returns physicians from cqm_apc_physician_mapping
     * with their APC count.
     */
    getSupervisingPhysicians: () =>
      fetchWithCredentials<SupervisingPhysicianOption[]>(
        `${ENDPOINT}/dashboard/supervising-physicians`
      ),

    /**
     * Get facility-level breakdown for a leader's measure performance.
     * Each row is a facility with numerator/denominator/rate for the given measure.
     */
    getLeaderMeasureFacilities: async (
      leaderEmail: string,
      measureName: string,
      year: number,
      quarter: number
    ): Promise<LeaderMeasureFacility[]> => {
      const qs = buildQueryString({ leader_email: leaderEmail, year, quarter })
      return fetchWithCredentials(`${ENDPOINT}/dashboard/leadership/measure/${measureName}/facilities${qs}`)
    },

    /**
     * Get patient-level list for a specific facility + measure under a leader.
     * Paginated — defaults to page 1 with 200 rows.
     */
    getLeaderMeasureFacilityPatients: async (
      leaderEmail: string,
      measureName: string,
      facilityId: number,
      year: number,
      quarter: number,
      page = 1,
      pageSize = 200
    ): Promise<LeaderMeasurePatientsResponse> => {
      const qs = buildQueryString({ leader_email: leaderEmail, year, quarter, page, page_size: pageSize })
      return fetchWithCredentials(`${ENDPOINT}/dashboard/leadership/measure/${measureName}/facility/${facilityId}/patients${qs}`)
    },

    /**
     * Get patient-level list for ALL facilities under a leader for a measure.
     * Flat list (no facility grouping). Paginated.
     */
    getLeaderMeasurePatients: async (
      leaderEmail: string,
      measureName: string,
      year: number,
      quarter: number,
      page = 1,
      pageSize = 200
    ): Promise<LeaderMeasurePatientsResponse> => {
      const qs = buildQueryString({ leader_email: leaderEmail, year, quarter, page, page_size: pageSize })
      return fetchWithCredentials(`${ENDPOINT}/dashboard/leadership/measure/${measureName}/patients${qs}`)
    },

    /**
     * Get all ACO patients in a leader's territory with per-measure
     * eligible/met flags for the outcome measures.
     */
    getLeaderAcoPatients: async (
      params: { year: number; quarter: number }
    ): Promise<LeaderAcoPatientsResponse> => {
      const qs = buildQueryString({ year: params.year, quarter: params.quarter })
      return fetchWithCredentials(`${ENDPOINT}/dashboard/leadership/aco-patients${qs}`)
    },

    /**
     * Get clinician-level audit summary under a leader for a given audit type.
     * Each row is a clinician with total/passing audit counts and pass rate.
     */
    getLeaderAuditClinicians: async (
      leaderEmail: string,
      auditType: string,
      year: number,
      quarter: number
    ): Promise<LeaderAuditClinician[]> => {
      const qs = buildQueryString({ leader_email: leaderEmail, audit_type: auditType, year, quarter })
      return fetchWithCredentials(`${ENDPOINT}/dashboard/leadership/audits/${auditType}/clinicians${qs}`)
    },
  },

  /**
   * Export endpoints (performance report download/email)
   */
  export: {
    /**
     * Generate and download Excel performance report as a Blob
     */
    download: async (
      providerEmail: string,
      year: number,
      quarter: number,
      signal?: AbortSignal
    ): Promise<Blob> => {
      const response = await fetch(`${ENDPOINT}/export/download`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider_email: providerEmail, year, quarter }),
        signal,
      })
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
        throw new Error(error.detail || `HTTP ${response.status}`)
      }
      return response.blob()
    },

    /**
     * Generate and download Excel territory report for a leader as a Blob.
     * Posts leader_email + year/quarter; returns the binary xlsx response.
     */
    downloadLeadershipReport: async (
      leaderEmail: string,
      year: number,
      quarter: number,
      signal?: AbortSignal
    ): Promise<Blob> => {
      const response = await fetch(`${ENDPOINT}/export/leadership/download`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leader_email: leaderEmail, year, quarter }),
        signal,
      })
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
        throw new Error(error.detail || `HTTP ${response.status}`)
      }
      return response.blob()
    },

    /**
     * Generate Excel performance report and email it
     */
    email: async (
      providerEmail: string,
      year: number,
      quarter: number,
      signal?: AbortSignal,
      toEmail?: string,
      ccEmail?: string,
    ): Promise<void> => {
      const payload: Record<string, unknown> = { provider_email: providerEmail, year, quarter }
      if (toEmail) payload.to_email = toEmail
      if (ccEmail) payload.cc_email = ccEmail
      await fetchWithCredentials<{ status: string }>(`${ENDPOINT}/export/email`, {
        method: 'POST',
        body: JSON.stringify(payload),
        signal,
      })
    },
  },

  /**
   * Config endpoints (program configuration from DB)
   */
  config: {
    /**
     * Get all provider-type configurations (award caps, part-time units, etc.)
     */
    getProgramConfig: (): Promise<ProgramConfigItem[]> =>
      fetchWithCredentials<ProgramConfigItem[]>(`${ENDPOINT}/config/program`),

    /**
     * Update financial fields for a provider type (admin only)
     * Returns the updated ProgramConfigItem after persisting changes
     */
    updateProgramConfig: (providerType: string, update: ProgramConfigUpdate): Promise<ProgramConfigItem> =>
      fetchWithCredentials<ProgramConfigItem>(`${ENDPOINT}/config/program/${encodeURIComponent(providerType)}`, {
        method: 'PUT',
        body: JSON.stringify(update),
      }),

    /**
     * Get paginated audit history for a provider type (admin only)
     */
    getConfigAudit: (providerType: string, params?: { offset?: number; limit?: number }): Promise<ConfigAuditResponse> =>
      fetchWithCredentials<ConfigAuditResponse>(
        `${ENDPOINT}/config/program/${encodeURIComponent(providerType)}/audit${buildQueryString(params ?? {})}`
      ),

    /**
     * Get all eligibility criteria rules (admin only)
     */
    getEligibilityCriteria: (): Promise<EligibilityCriteriaRule[]> =>
      fetchWithCredentials<EligibilityCriteriaRule[]>(`${ENDPOINT}/config/eligibility-criteria`),

    /**
     * Update a single eligibility criterion by rule ID (admin only)
     */
    updateEligibilityCriteriaRule: (ruleId: number, update: EligibilityCriterionUpdate): Promise<EligibilityCriteriaRule> =>
      fetchWithCredentials<EligibilityCriteriaRule>(`${ENDPOINT}/config/eligibility-criteria/${ruleId}`, {
        method: 'PUT',
        body: JSON.stringify(update),
      }),

    /**
     * Get paginated audit history for a specific eligibility criterion (admin only)
     */
    getEligibilityCriteriaAudit: (ruleId: number, params?: { offset?: number; limit?: number }): Promise<EligibilityCriteriaAuditResponse> =>
      fetchWithCredentials<EligibilityCriteriaAuditResponse>(
        `${ENDPOINT}/config/eligibility-criteria/${ruleId}/audit${buildQueryString(params ?? {})}`
      ),

    /**
     * Get measure schedule for a given year/quarter (admin only)
     */
    getMeasureSchedule: (year: number, quarter: number, bonusEligibleType?: string): Promise<MeasureScheduleItem[]> => {
      const params: Record<string, string> = { year: String(year), quarter: String(quarter) }
      if (bonusEligibleType) params.bonus_eligible_type = bonusEligibleType
      return fetchWithCredentials<MeasureScheduleItem[]>(`${ENDPOINT}/config/measure-schedule${buildQueryString(params)}`)
    },

    getMeasureNames: (): Promise<string[]> =>
      fetchWithCredentials<string[]>(`${ENDPOINT}/config/measure-schedule/measures`),

    createMeasureSchedule: (body: MeasureScheduleCreate): Promise<MeasureScheduleItem> =>
      fetchWithCredentials<MeasureScheduleItem>(`${ENDPOINT}/config/measure-schedule`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    updateMeasureSchedule: (id: number, body: MeasureScheduleUpdate): Promise<MeasureScheduleItem> =>
      fetchWithCredentials<MeasureScheduleItem>(`${ENDPOINT}/config/measure-schedule/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),

    deleteMeasureSchedule: (id: number): Promise<void> =>
      fetchWithCredentials<void>(`${ENDPOINT}/config/measure-schedule/${id}`, {
        method: 'DELETE',
      }),

    getPimsDistribution: (): Promise<PimsDistribution> =>
      fetchWithCredentials<PimsDistribution>(`${ENDPOINT}/config/pims-distribution`),
  },

  /**
   * Eligibility management endpoints (admin roster view and edit)
   */
  eligibility: {
    /**
     * Get the full HR employee roster (read-only, no quarter filter)
     */
    getEmployeeRoster: (): Promise<EmployeeRosterResponse> =>
      fetchWithCredentials<EmployeeRosterResponse>(
        `${ENDPOINT}/eligibility/employee-roster`
      ),

    /**
     * Get all quarters that have eligibility data, newest first
     */
    getAvailableQuarters: (): Promise<{ year: number; quarter: number }[]> =>
      fetchWithCredentials<{ year: number; quarter: number }[]>(
        `${ENDPOINT}/eligibility/available-quarters`
      ),

    /**
     * Get eligibility roster for a year/quarter with summary stats
     */
    getRoster: (year: number, quarter: number): Promise<EligibilityRosterResponse> =>
      fetchWithCredentials<EligibilityRosterResponse>(
        `${ENDPOINT}/eligibility/roster${buildQueryString({ year, quarter })}`
      ),

    /**
     * Update eligibility fields for a single provider row (admin only)
     * Returns the updated EligibilityRow after persisting changes
     */
    updateEligibility: (id: number, update: EligibilityUpdate): Promise<EligibilityRow> =>
      fetchWithCredentials<EligibilityRow>(
        `${ENDPOINT}/eligibility/${id}`,
        { method: 'PUT', body: JSON.stringify(update) }
      ),

    /**
     * Get seed status for a given year/quarter (whether rows already exist)
     */
    getSeedStatus: (year: number, quarter: number): Promise<SeedStatusResponse> =>
      fetchWithCredentials<SeedStatusResponse>(
        `${ENDPOINT}/eligibility/seed/status${buildQueryString({ year, quarter })}`
      ),

    /**
     * Get a preview of what seeding would produce for a given year/quarter
     */
    getSeedPreview: (year: number, quarter: number): Promise<SeedPreviewResponse> =>
      fetchWithCredentials<SeedPreviewResponse>(
        `${ENDPOINT}/eligibility/seed/preview${buildQueryString({ year, quarter })}`
      ),

    /**
     * Execute the seed operation to populate the eligibility roster (admin only).
     * Accepts optional per-provider overrides applied on top of HR data.
     */
    executeSeed: (year: number, quarter: number, overrides: SeedOverride[] = []): Promise<SeedExecuteResponse> =>
      fetchWithCredentials<SeedExecuteResponse>(
        `${ENDPOINT}/eligibility/seed/execute`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ year, quarter, overrides }),
        }
      ),


    /**
     * Add NPI-holding Active/LOA employees missing from eligibility history.
     * Inserts them as ineligible with no bonus_eligible_type.
     */
    addMissingClinicians: (year: number, quarter: number): Promise<AddMissingResponse> =>
      fetchWithCredentials<AddMissingResponse>(
        `${ENDPOINT}/eligibility/roster/add-missing${buildQueryString({ year, quarter })}`,
        { method: 'POST' }
      ),

    /**
     * Bulk mark multiple eligibility rows as eligible (admin only).
     * Requires a reason and bonus_eligible_type applied to all selected rows.
     */
    bulkMarkEligible: (request: BulkMarkEligibleRequest): Promise<BulkMarkEligibleResponse> =>
      fetchWithCredentials<BulkMarkEligibleResponse>(
        `${ENDPOINT}/eligibility/bulk/mark-eligible`,
        { method: 'POST', body: JSON.stringify(request) }
      ),

    /**
     * Export the eligibility roster as an Excel blob (admin only)
     * Uses a 90-second timeout with AbortController
     */
    exportRoster: async (year: number, quarter: number): Promise<Blob> => {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 90000)
      try {
        const response = await fetch(
          `${ENDPOINT}/eligibility/roster/export${buildQueryString({ year, quarter })}`,
          { credentials: 'include', signal: controller.signal }
        )
        if (!response.ok) throw new Error(`Export failed: HTTP ${response.status}`)
        return response.blob()
      } finally {
        clearTimeout(timeoutId)
      }
    },

    /**
     * Get the validation workflow status for a given year/quarter
     */
    getValidationStatus: (year: number, quarter: number): Promise<ValidationStatus> =>
      fetchWithCredentials<ValidationStatus>(
        `${ENDPOINT}/eligibility/validation/status${buildQueryString({ year, quarter })}`
      ),

    /**
     * Transition the validation workflow to the next state (admin only)
     */
    transitionValidation: (request: ValidationTransitionRequest): Promise<ValidationStatus> =>
      fetchWithCredentials<ValidationStatus>(
        `${ENDPOINT}/eligibility/validation/transition`,
        { method: 'POST', body: JSON.stringify(request) }
      ),
  },

  /**
   * QA/Debug admin endpoints (admin-only)
   */
  qaDebug: {
    /**
     * Get the full provider grid for a given year/quarter.
     * Each row is a LeadershipProviderRow with data_source populated.
     * Requires equip.admin role.
     */
    grid: (params: { year: number; quarter: number }) =>
      fetchWithCredentials<GridResponse>(
        `${ENDPOINT}/admin/qa-debug/grid${buildQueryString(params)}`
      ),

    /**
     * Get source freshness for upstream, cache, and snapshot tables.
     * Returns age, last refresh time, row count, and status for each table.
     * Requires equip.admin role.
     */
    sourceFreshness: (params: { year: number; quarter: number }) =>
      fetchWithCredentials<SourceFreshnessResponse>(
        `${ENDPOINT}/admin/qa-debug/source-freshness${buildQueryString(params)}`
      ),

    /**
     * Get provider profile summary (name, care team, facility, measure scores).
     * Requires equip.admin role.
     */
    getProviderProfile: (npi: string, params: { year: number; quarter: number }) =>
      fetchWithCredentials<ProviderProfile>(
        `${ENDPOINT}/admin/qa-debug/provider/${encodeURIComponent(npi)}/profile${buildQueryString(params)}`
      ),

    /**
     * Get measure constituents for a provider (aggregate + patient list + raw SQL).
     * Requires equip.admin role.
     */
    getMeasureConstituents: (
      npi: string,
      measure: string,
      params: { year: number; quarter: number }
    ) =>
      fetchWithCredentials<MeasureConstituents>(
        `${ENDPOINT}/admin/qa-debug/provider/${encodeURIComponent(npi)}/measure/${encodeURIComponent(measure)}${buildQueryString(params)}`
      ),
  },

  /**
   * Scope endpoints (manager/clinician dropdown data)
   */
  scope: {
    /**
     * Get managers for Leadership page dropdown (admin only)
     */
    getManagers: (params?: { year?: number; quarter?: number }) =>
      fetchWithCredentials<ManagerOption[]>(
        `${ENDPOINT}/scope/managers${buildQueryString(
          params
            ? {
                year: params.year,
                quarter: params.quarter,
              }
            : {}
        )}`
      ),

    /**
     * Get clinicians for Clinician page dropdown (role-scoped)
     * Admin: all clinicians. Leadership: subordinates only. Read: empty.
     */
    getClinicians: (params?: { year?: number; quarter?: number }) =>
      fetchWithCredentials<ClinicianOption[]>(
        `${ENDPOINT}/scope/clinicians${buildQueryString(
          params
            ? {
                year: params.year,
                quarter: params.quarter,
              }
            : {}
        )}`
      ),

    /**
     * Check if the authenticated user has their own EQuIP performance data.
     * Used by the Clinician page to default leadership+provider users to their own data.
     */
    getSelfIsProvider: (params?: { year?: number; quarter?: number }) =>
      fetchWithCredentials<{ is_provider: boolean }>(
        `${ENDPOINT}/scope/self-is-provider${buildQueryString(
          params
            ? {
                year: params.year,
                quarter: params.quarter,
              }
            : {}
        )}`
      ),

    getSeniorLeads: () =>
      fetchWithCredentials<{ senior_lead_email: string; senior_lead_name: string }[]>(
        `${ENDPOINT}/scope/senior-leads`
      ),
  },

  awards: {
    /**
     * List all year/quarter options that have award data.
     */
    getAvailableQuarters: (): Promise<AwardQuartersResponse> =>
      fetchWithCredentials<AwardQuartersResponse>(`${ENDPOINT}/awards/quarters`),

    /**
     * Fetch award grid rows for a specific quarter.
     */
    getGrid: (year: number, quarter: number): Promise<AwardGridResponse> =>
      fetchWithCredentials<AwardGridResponse>(
        `${ENDPOINT}/awards/grid${buildQueryString({ year, quarter })}`
      ),

    /**
     * Update editable fields on an award row.
     * source: 'history' | 'snapshot' — identifies which DB table to write.
     */
    updateRow: (
      id: number,
      source: 'history' | 'snapshot',
      update: AwardUpdate
    ): Promise<{ ok: boolean }> =>
      fetchWithCredentials<{ ok: boolean }>(
        `${ENDPOINT}/awards/${id}${buildQueryString({ source })}`,
        { method: 'PUT', body: JSON.stringify(update) }
      ),

    /**
     * Fetch field-level edit history for a single award record.
     */
    getHistory: (id: number, source: 'history' | 'snapshot'): Promise<AwardHistoryResponse> =>
      fetchWithCredentials<AwardHistoryResponse>(
        `${ENDPOINT}/awards/${id}/history${buildQueryString({ source })}`
      ),

    /**
     * Download award grid Excel file. 90-second timeout.
     */
    exportGrid: async (year: number, quarter: number): Promise<Blob> => {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 90000)
      try {
        const response = await fetch(
          `${ENDPOINT}/awards/export${buildQueryString({ year, quarter })}`,
          { credentials: 'include', signal: controller.signal }
        )
        if (!response.ok) throw new Error(`Export failed: HTTP ${response.status}`)
        return response.blob()
      } finally {
        clearTimeout(timeoutId)
      }
    },
  },

  /**
   * Ask-Question (RAG chatbot) endpoint.
   *
   * POST /api/equip/ask-question
   * Body: { question: string }
   * Response: { status, answer, citations: string[], sources?: EquipCitationSource[], reason? }
   */
  askQuestion: (question: string) =>
    fetchWithCredentials<AskQuestionResponse>(`${ENDPOINT}/ask-question`, {
      method: 'POST',
      body: JSON.stringify({ question }),
    }),
}

/** A single clickable source reference backing an answer. */
export interface EquipCitationSource {
  /** Display label, e.g. "Slide 12" or "Eligibility Policy.pdf — page 3". */
  label: string
  /** Underlying document file name, when known. */
  file_name?: string
  /** 1-based page/slide number, when known. */
  page?: number | null
  /** 1-based cited pages for this source, in relevance order. */
  pages?: number[]
  /** Clickable URL that opens the document at the cited page. Null when unavailable. */
  url?: string | null
}

/** Response shape from POST /api/equip/ask-question. */
export interface AskQuestionResponse {
  status: 'answered' | 'no_context'
  answer: string
  /** Human-readable source labels, e.g. "Slide 12". Kept for backward compatibility. */
  citations: string[]
  /** Structured, clickable sources. Preferred over `citations` when present. */
  sources?: EquipCitationSource[]
  reason?: string
}

export default equipApi
